import { IAuthRepository, UserDetailsResponse } from "@/core/repositories/auth";
import { PrivateUserDetailsHttp } from "@/infrastructure/adapters/http";
import { SessionStorageAdapter } from "@/infrastructure/adapters/sessionStorage";
import { JsonSerializer } from "@/infrastructure/serializers/JsonSerializer";
import { ApiEndpoints } from "@/infrastructure/services/config";
import { requestContext } from "@/infrastructure/services/logger";

interface IUserDetailsResponse {
  userInfo: UserDetailsResponse["userProps"];
  userBrands: UserDetailsResponse["userBrandsProps"];
  userCurrentBrand: UserDetailsResponse["userCurrentBrandProps"];
}

export class AuthRepository implements IAuthRepository {
  private readonly sessionStorage: SessionStorageAdapter;
  private readonly USER_INFO = "info";
  private readonly USER_BRANDS = "userBrands";
  private readonly USER_CURRENT_BRAND = "userCurrentBrand";
  private readonly USER_CURRENT_BRAND_KEY = "userCurrentBrandKey";

  constructor() {
    this.sessionStorage = new SessionStorageAdapter(new JsonSerializer(), "irAppUser");
  }

  async getUserDetails(brandKey?: string): Promise<UserDetailsResponse> {
    let userCurrentBrandKey = await this.getUserCurrentBrandKeyFromStorage();
    if (brandKey) {
      userCurrentBrandKey = brandKey;
    }

    if (userCurrentBrandKey) {
      const userBrands = await this.getUserBrandsFromStorage();
      if (userBrands && userBrands.length > 0 && !userBrands.some(brand => brand.brandKey === userCurrentBrandKey)) {
        userCurrentBrandKey = null;
      }
    }

    const url = userCurrentBrandKey ? `${ApiEndpoints.USER_WORKSPACE}?brandKey=${userCurrentBrandKey}` : ApiEndpoints.USER_WORKSPACE;
    const userDetailsResponse = await new PrivateUserDetailsHttp().get<IUserDetailsResponse>(url);
    return {
      userProps: userDetailsResponse.userInfo,
      userBrandsProps: userDetailsResponse.userBrands,
      userCurrentBrandProps: userDetailsResponse.userCurrentBrand,
    };
  }

  async saveUserToStorage(props: UserDetailsResponse): Promise<void> {
    const expirationDuration = 15 * 60; // 15 minutes

    const sessionStorage = this.sessionStorage;

    /* indexDB storage */
    // Save user to indexDB storage
    await sessionStorage.store(
      {
        key: this.USER_INFO,
        value: props.userProps,
      },
      expirationDuration
    );

    // Save user current brand list to indexDB storage
    await sessionStorage.store(
      {
        key: this.USER_BRANDS,
        value: props.userBrandsProps,
      },
      expirationDuration
    );


    /* session storage */
    // Save user current brand to session storage
    await sessionStorage.store(
      {
        key: this.USER_CURRENT_BRAND,
        value: props.userCurrentBrandProps,
      },
      expirationDuration
    );

    // Save user current brand key to session storage
    await sessionStorage.store(
      {
        key: this.USER_CURRENT_BRAND_KEY,
        value: props.userCurrentBrandProps.brandKey,
      }
    );
  }

  async getUserInfoFromStorage(): Promise<UserDetailsResponse["userProps"] | null> {
    const userPropsItem = await this.sessionStorage.retrieve<UserDetailsResponse["userProps"]>(this.USER_INFO, true);
    return userPropsItem?.value ?? null;
  }

  async getUserCurrentBrandFromStorage(): Promise<UserDetailsResponse["userCurrentBrandProps"] | null> {
    const userCurrentBrandPropsItem = await this.sessionStorage.retrieve<UserDetailsResponse["userCurrentBrandProps"]>(this.USER_CURRENT_BRAND, true);
    return userCurrentBrandPropsItem?.value ?? null;
  }

  async getUserCurrentBrandKeyFromStorage(): Promise<string | null> {
    const userCurrentBrandKeyItem = await this.sessionStorage.retrieve<string>(this.USER_CURRENT_BRAND_KEY);
    return userCurrentBrandKeyItem?.value ?? null;
  }

  async getUserBrandsFromStorage(): Promise<UserDetailsResponse["userBrandsProps"] | null> {
    const userBrandsPropsItem = await this.sessionStorage.retrieve<UserDetailsResponse["userBrandsProps"]>(this.USER_BRANDS, true);
    return userBrandsPropsItem?.value ?? null;
  }

  async isAuthenticated(): Promise<boolean> {
    const userPropsItem = await this.sessionStorage.retrieve<UserDetailsResponse["userProps"]>(this.USER_INFO, true);
    const userCurrentBrandKeyItem = await this.sessionStorage.retrieve<string>(this.USER_CURRENT_BRAND_KEY);
    if (!userCurrentBrandKeyItem || !userPropsItem?.value) {
      return false;
    }
    return true;
  }

  async logout(): Promise<void> {
    await this.sessionStorage.clear();
    /* logger context */
    requestContext.run({
      user: null,
      brand: null,
    }, () => { });
  }
}
